/**
 * Nomenclatorul instrumentelor de promovare: planogramă, casetă, vitrină.
 *
 * Nu are ștergere, deliberat. Un instrument se trece pe inactiv: legătura de pe expunere e
 * `SetNull`, deci o ștergere ar lăsa tăcut fără instrument exact expunerile vechi pe care nu le
 * mai verifică nimeni. Inactiv înseamnă „nu se mai poate alege de acum înainte", iar istoria
 * rămâne citibilă.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'

interface Instrument {
  id: number
  denumire: string
  activ: boolean
  ordine: number
}

export function SetariInstrumentePromovarePage() {
  const qc = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const poateScrie = permissions.includes('settings.promo_instruments:write')

  const [denumireNoua, setDenumireNoua] = useState('')

  const lista = useQuery<{ instrumente: Instrument[] }>({
    queryKey: ['instrumente-promovare'],
    queryFn: async () =>
      (await api.get<{ instrumente: Instrument[] }>('/marketing/instrumente')).data,
  })

  function eroare(err: unknown): void {
    const mesaj = (err as { response?: { data?: { error?: { message?: string } } } })?.response
      ?.data?.error?.message
    toast.error(mesaj ?? 'Nu s-a putut salva.')
  }

  const adauga = useMutation({
    mutationFn: async (denumire: string) =>
      (await api.post('/marketing/instrumente', { denumire })).data,
    onSuccess: () => {
      setDenumireNoua('')
      void qc.invalidateQueries({ queryKey: ['instrumente-promovare'] })
      toast.success('Instrument adăugat.')
    },
    onError: eroare,
  })

  const modifica = useMutation({
    mutationFn: async ({ id, ...date }: { id: number; denumire?: string; activ?: boolean }) =>
      (await api.patch(`/marketing/instrumente/${id}`, date)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['instrumente-promovare'] }),
    onError: eroare,
  })

  const instrumente = lista.data?.instrumente ?? []

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Setări → Instrumente de promovare</h1>

      <Card description="Ce se poate cumpăra la un partener: planogramă, casetă, vitrină. Lista se completează ori de câte ori apare un instrument nou care costă bani. Un instrument nu se șterge — se trece pe inactiv, ca expunerile vechi care îl folosesc să rămână citibile.">
        {poateScrie && (
          <form
            className="mb-4 flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (denumireNoua.trim()) adauga.mutate(denumireNoua.trim())
            }}
          >
            <input
              value={denumireNoua}
              onChange={(e) => setDenumireNoua(e.target.value)}
              placeholder="Instrument nou — ex. Stand de podea"
              maxLength={60}
              className="min-w-0 flex-1 rounded border border-b4l-border px-2 py-1.5 text-sm"
            />
            <Button type="submit" loading={adauga.isPending} disabled={!denumireNoua.trim()}>
              Adaugă
            </Button>
          </form>
        )}

        {lista.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {!lista.isLoading && instrumente.length === 0 && (
          <p className="text-sm text-b4l-text-muted">Niciun instrument încă.</p>
        )}

        <ul className="space-y-2">
          {instrumente.map((i) => (
            <li
              key={i.id}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-b4l-border p-3"
            >
              <span
                className={`flex-1 text-sm ${i.activ ? 'text-b4l-text' : 'text-b4l-text-muted line-through'}`}
              >
                {i.denumire}
              </span>
              {!i.activ && (
                <span className="rounded-full bg-b4l-bg px-2 py-0.5 text-xs text-b4l-text-muted">
                  inactiv
                </span>
              )}
              {poateScrie && (
                <Button
                  variant="secondary"
                  className="!px-2 !py-1 !text-xs"
                  loading={modifica.isPending}
                  title={
                    i.activ
                      ? 'Nu se mai poate alege de acum înainte. Expunerile vechi rămân neatinse.'
                      : 'Redevine disponibil la alegere.'
                  }
                  onClick={() => modifica.mutate({ id: i.id, activ: !i.activ })}
                >
                  {i.activ ? 'Dezactivează' : 'Reactivează'}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  )
}
